import Board from "components/Common/Board/Board";
import Button from "components/foundation/Button";
import Divider from "components/foundation/Divider";
import { ROUTER_PATH } from "config/path";
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import styled from "styled-components";
import "@toast-ui/editor/dist/toastui-editor-viewer.css";
import { Viewer } from "@toast-ui/react-editor";
import { FilterListLayout } from "components/Common/PageFilterPannel/FilterListItem";
import Input from "components/foundation/Input/Input";
import { match } from "ts-pattern";
import Modal from "components/foundation/Modal/Modal";
import { NoticeDto } from "apis/data-contracts";
import useUserDetailQuery from "features/user/hooks/queries/useUserDetailQuery";
import useNoticeDeleteMutation from "features/notice/hooks/mutations/useNoticeDeleteMutation";
import addHttpsToAnchorTag from "utils/addHttpsToAnchorTag";

interface NoticeDashboardDetailPageProps {
  noticeDetail: NoticeDto;
}

const NoticeDashboardDetailPage = ({
  noticeDetail,
}: NoticeDashboardDetailPageProps) => {
  const navigate = useNavigate();

  const { data: userData } = useUserDetailQuery();
  const { mutate: deleteMutate } = useNoticeDeleteMutation();

  const isMine = userData?.uuid === noticeDetail.createdUser.uuid;

  const handleDeleteNotice = () => {
    //삭제 API
    deleteMutate(noticeDetail.uuid, {
      onSuccess: () => {
        navigate(ROUTER_PATH.notice.dashboard._path);
      },
    });
  };

  return (
    <NoticeDashboardDetailPageLayout>
      <NoticeDashboardDetailPageHeader>
        <h1>공지사항 조회</h1>

        {match(isMine)
          .with(true, () => (
            <div
              style={{
                display: "flex",
                gap: "1rem",
              }}
            >
              <Link to={ROUTER_PATH.notice.dashboard._path}>
                <Button variant="tertiary" size="large">
                  뒤로
                </Button>
              </Link>
              <Link to={ROUTER_PATH.notice.dashboard.edit(noticeDetail.uuid)}>
                <Button variant="primary" size="large">
                  수정
                </Button>
              </Link>
              <Modal.Root>
                <Modal.Trigger asChild>
                  <Button variant="tertiary-neg" size="large">
                    삭제
                  </Button>
                </Modal.Trigger>
                <Modal.Body
                  title="정말 삭제하시겠습니까?"
                  onCancel
                  onConfirm={handleDeleteNotice}
                />
              </Modal.Root>
            </div>
          ))
          .otherwise(() => (
            <Link to={ROUTER_PATH.notice.dashboard._path}>
              <Button variant="tertiary" size="large">
                뒤로
              </Button>
            </Link>
          ))}
      </NoticeDashboardDetailPageHeader>
      <NoticeDashboardDetailContentBoard>
        <h2>{noticeDetail.title}</h2>
        <Divider />
        <Viewer
          initialValue={addHttpsToAnchorTag(noticeDetail.content)}
          key={noticeDetail.content}
        />
      </NoticeDashboardDetailContentBoard>
      <NoticeDashboardDetailAttachBoard>
        <FilterListLayout>
          <h2>첨부파일</h2>
          <div>
            <Input
              onClick={() => {
                noticeDetail.noticeAttachmentFileList[0]?.fileUrl &&
                  window.open(noticeDetail.noticeAttachmentFileList[0].fileUrl);
              }}
              value={
                noticeDetail.noticeAttachmentFileList[0]?.fileName ??
                "첨부된 파일 없음"
              }
              readOnly
              onFocus={(e) => e.target.blur()}
              style={{
                width: "100%",
              }}
            />
          </div>
        </FilterListLayout>
      </NoticeDashboardDetailAttachBoard>
    </NoticeDashboardDetailPageLayout>
  );
};

export default NoticeDashboardDetailPage;

const NoticeDashboardDetailPageLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const NoticeDashboardDetailPageHeader = styled(Board)`
  padding: 3rem 4rem;
  display: flex;
  justify-content: space-between;

  h1 {
    font-size: 28px;
    font-weight: 600;
    line-height: 36px; /* 128.571% */
    letter-spacing: -0.56px;
  }
`;

const NoticeDashboardDetailContentBoard = styled(Board)`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  padding: 4rem;

  h2 {
    color: ${({ theme }) => theme.color.text.default};
    font-size: 24px;
    font-weight: 600;
    line-height: 36px; /* 150% */
    letter-spacing: -0.72px;
  }
`;

const NoticeDashboardDetailAttachBoard = styled(Board)`
  padding: 4rem;
`;
